/**
 * BarcodeZoomModal - Shows a barcode zoomed 2.5x in a centered modal
 * Uses Headless UI Dialog for ESC/click-outside close behavior
 */
import { Fragment, ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';

interface BarcodeZoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  productName?: string;
}

export const BarcodeZoomModal: React.FC<BarcodeZoomModalProps> = ({
  isOpen,
  onClose,
  children,
  productName,
}) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60" />
        </Transition.Child>

        {/* Panel */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative bg-white border-4 border-orange-500 rounded-2xl shadow-2xl p-24">
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>

                {/* Product name */}
                {productName && (
                  <div className="text-center mb-6">
                    <p className="text-sm font-semibold text-gray-700">{productName}</p>
                  </div>
                )}

                {/* Zoomed barcode — padding compensates for scale() not affecting layout */}
                <div className="flex items-center justify-center p-32">
                  <div style={{ transform: 'scale(2.5)', transformOrigin: 'center' }}>
                    {children}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
